import React, { useCallback, useState } from "react";
import DataGrid from "@dtci/spellbook/DataGrid";
import Button from "@dtci/spellbook/Button";
import styles from "./pptAuditLogGrid.module.scss";
import { dataGrid } from "../../../../components/AGGrid/details-table.module.scss";
import { defaultColumnDef, columnDefs } from "./config/columns";
import { usePPTState } from "tap/store/ppt/usePPT";
import { usePPTToasts } from "tap/pages/PPT/hooks/usePPTToasts";
import { exportPromotionAuditLog } from "ppt/api";

export const PPTAuditLogGrid = ({ gridRef, onPaginationChange }) => {
  const {
    promotionDetail,
    promotionEvents,
    promotionEventsSearch,
    promotionEventsPagination,
  } = usePPTState();
  const { showErrorToast } = usePPTToasts();
  const pendingParamsRef = React.useRef(null);
  const previousSearchRef = React.useRef(promotionEventsSearch);
  const searchRef = React.useRef(promotionEventsSearch);

  const [exportingAuditLog, setExportingAuditLog] = useState(false);

  const handleExportAuditLog = useCallback(async () => {
    if (!promotionDetail?.id) return;
    setExportingAuditLog(true);
    try {
      await exportPromotionAuditLog(promotionDetail.id);
    } catch (error) {
      console.error("Failed to export audit log:", error);
      showErrorToast("Failed to export audit log. Please try again.");
    } finally {
      setExportingAuditLog(false);
    }
  }, [promotionDetail?.id, showErrorToast]);

  // Keep search ref in sync
  React.useEffect(() => {
    searchRef.current = promotionEventsSearch;
  }, [promotionEventsSearch]);

  // Create server-side datasource
  const datasource = React.useMemo(
    () => ({
      getRows: (params) => {
        const { startRow, endRow, sortModel } = params.request;
        const pageSize = endRow - startRow;

        // Store the params callback for when data arrives
        pendingParamsRef.current = params;

        // Extract sort information
        const sortBy =
          sortModel && sortModel.length > 0 ? sortModel[0].colId : null;
        const sortOrder =
          sortModel && sortModel.length > 0 ? sortModel[0].sort : null;

        // Use quick search filter - read from ref to get current value
        const filterBy = searchRef.current || null;

        // Request data from server
        if (onPaginationChange) {
          onPaginationChange({
            offset: startRow,
            limit: pageSize,
            sortBy,
            sortOrder,
            filterBy,
          });
        }
      },
    }),
    [onPaginationChange],
  );

  // Trigger refresh when quick filter changes
  React.useEffect(() => {
    if (
      previousSearchRef.current !== promotionEventsSearch &&
      gridRef?.current?.api
    ) {
      previousSearchRef.current = promotionEventsSearch;
      gridRef.current.api.refreshServerSide({ purge: true });
    }
  }, [promotionEventsSearch, gridRef]);

  // When data arrives from Redux, notify AG Grid
  React.useEffect(() => {
    if (pendingParamsRef.current && promotionEvents) {
      pendingParamsRef.current.success({
        rowData: promotionEvents,
        rowCount: promotionEventsPagination.total,
      });
      pendingParamsRef.current = null;
    }
  }, [promotionEvents, promotionEventsPagination.total]);

  // Initial datasource setup
  const onGridReady = React.useCallback(
    (params) => {
      params.api.setGridOption("serverSideDatasource", datasource);
    },
    [datasource],
  );

  return (
    <div className={styles.pptAuditLogGridWrapper}>
      <div className={styles.headerRow}>
        <h4 className={styles.header}>Audit Log</h4>
        <Button
          styleType="link"
          iconPlacement="before"
          iconType="fa-download"
          onClick={handleExportAuditLog}
          isFetching={exportingAuditLog}
          title="Export audit log to Excel"
        >
          Export
        </Button>
      </div>
      <DataGrid
        className={`${dataGrid} ${styles.pptAuditLogGrid}`}
        autoSizing
        agGrid={{
          ref: gridRef,
          headerHeight: 45,
          suppressContextMenu: true,
          getRowId: (params) => String(params.data?.id),
          rowModelType: "serverSide",
          pagination: true,
          paginationPageSize: promotionEventsPagination.limit,
          paginationPageSizeSelector: [25, 50, 100],
          cacheBlockSize: promotionEventsPagination.limit,
          maxBlocksInCache: 10,
          onGridReady: onGridReady,
        }}
        bordered="none"
        columnDefs={columnDefs}
        defaultColumnDef={defaultColumnDef}
        noResultsMessage={"No Audit Events Found"}
        sortable
      />
    </div>
  );
};
